/**
 * Verticals mapping utilities
 *
 * The codebase historically used two representations for verticals:
 * - UI format:   pest, fiber, life_insurance, solar, alarms, home_improvement
 * - DB format:   pest_control, fiber_internet, life_insurance, solar, alarms, home_improvement
 *
 * This module provides a single, shared conversion layer so the UI stays consistent
 * and all writes back to the DB use the canonical DB format.
 */
export const UI_VERTICALS = [
  "alarms",
  "fiber",
  "home_improvement",
  "life_insurance",
  "pest",
  "solar",
  "window_cleaning",
] as const;

export type UiVertical = (typeof UI_VERTICALS)[number];

export const DB_VERTICALS = [
  "alarms",
  "fiber_internet",
  "home_improvement",
  "life_insurance",
  "pest_control",
  "solar",
  "window_cleaning",
] as const;

export type DbVertical = (typeof DB_VERTICALS)[number];

export const DB_TO_UI_VERTICAL_MAP: Record<DbVertical, UiVertical> = {
  alarms: "alarms",
  fiber_internet: "fiber",
  home_improvement: "home_improvement",
  life_insurance: "life_insurance",
  pest_control: "pest",
  solar: "solar",
  window_cleaning: "window_cleaning",
};

export const UI_TO_DB_VERTICAL_MAP: Record<UiVertical, DbVertical> = {
  alarms: "alarms",
  fiber: "fiber_internet",
  home_improvement: "home_improvement",
  life_insurance: "life_insurance",
  pest: "pest_control",
  solar: "solar",
  window_cleaning: "window_cleaning",
};

function isUiVertical(value: string): value is UiVertical {
  return (UI_VERTICALS as readonly string[]).includes(value);
}

function isDbVertical(value: string): value is DbVertical {
  return (DB_VERTICALS as readonly string[]).includes(value);
}

/**
 * Convert a vertical value of unknown origin to UI format.
 * Returns undefined for unknown/invalid values.
 */
export function toUIVertical(value: string): UiVertical | undefined {
  if (isUiVertical(value)) return value;
  if (isDbVertical(value)) return DB_TO_UI_VERTICAL_MAP[value];
  return undefined;
}

/**
 * Convert a vertical value of unknown origin to DB format.
 * Returns undefined for unknown/invalid values.
 */
export function toDBVertical(value: string): DbVertical | undefined {
  if (isDbVertical(value)) return value;
  if (isUiVertical(value)) return UI_TO_DB_VERTICAL_MAP[value];
  return undefined;
}

/**
 * Normalize an array of vertical values (DB or UI format) to UI format.
 * Unknown values are dropped.
 */
export function normalizeVerticalsToUI(values: readonly string[] | null | undefined): UiVertical[] {
  if (!values?.length) return [];
  return values
    .map((v) => toUIVertical(v))
    .filter((v): v is UiVertical => v !== undefined);
}

/**
 * Normalize an array of vertical values (DB or UI format) to DB format.
 * Unknown values are dropped.
 */
export function normalizeVerticalsToDB(values: readonly string[] | null | undefined): DbVertical[] {
  if (!values?.length) return [];
  return values
    .map((v) => toDBVertical(v))
    .filter((v): v is DbVertical => v !== undefined);
}


